import React, { createContext, useCallback, useContext, useEffect, useReducer } from 'react';
import { ProductAttribute, ProductVersion, SaleItemDimension, Unit } from 'models';
// import { SaleItemDimension } from 'models/SaleOrder';
import { Cart, CartAction, CartItem, CartPOSContextType, CartState, CartStatus } from 'types/common';
const CART_STORAGE_KEY = 'liemlan_pos_carts';

// Create context
const CartPOSContext = createContext<CartPOSContextType | null>(null);

// Reducer function
const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case 'ADD_CART': {
      // Limit to maximum 3 carts
      if (state.carts.length >= 3) return state;
      const newCartId = Date.now().toString();
      const newCart: Cart = {
        id: newCartId,
        name: '#' + (action.payload.name || newCartId.substring(8, 13)),
        status: CartStatus.PRODUCT_SELECTION,
        items: [],
        totalAmount: 0,
      };

      return {
        ...state,
        carts: [...state.carts, newCart],
        activeCartId: newCart.id,
      };
    }

    case 'REMOVE_CART': {
      const updatedCarts = state.carts.filter((cart) => cart.id !== action.payload.id);
      const activeCartId =
        state.activeCartId === action.payload.id
          ? updatedCarts.length > 0
            ? updatedCarts[0].id
            : null
          : state.activeCartId;

      return {
        ...state,
        carts: updatedCarts,
        activeCartId,
      };
    }

    case 'SET_ACTIVE_CART': {
      return {
        ...state,
        activeCartId: action.payload.id,
      };
    }

    case 'UPDATE_CART_STATUS': {
      return {
        ...state,
        carts: state.carts.map((cart) =>
          cart.id === action.payload.id ? { ...cart, status: action.payload.status } : cart,
        ),
      };
    }

    case 'ADD_ITEM': {
      const { cartId, item } = action.payload;

      return {
        ...state,
        carts: state.carts.map((cart) => {
          if (cart.id !== cartId) return cart;

          // Create new item with ID
          const newItem: CartItem = {
            id: crypto.randomUUID(),
            ...item,
            totalPrice: item.quantity * item.unitPrice,
          };

          // Calculate new total
          const newTotal = cart.items.reduce((sum, item) => sum + item.totalPrice, 0) + newItem.totalPrice;

          return {
            ...cart,
            items: [...cart.items, newItem],
            totalAmount: newTotal,
          };
        }),
      };
    }

    case 'UPDATE_ITEM': {
      const { cartId, itemId, updates } = action.payload;

      return {
        ...state,
        carts: state.carts.map((cart) => {
          if (cart.id !== cartId) return cart;

          const updatedItems = cart.items.map((item) => {
            if (item.id !== itemId) return item;

            // Update item and recalculate total price
            const updatedItem = {
              ...item,
              ...updates,
              totalPrice:
                updates.quantity !== undefined || updates.unitPrice !== undefined
                  ? (updates.quantity !== undefined ? updates.quantity : item.quantity) *
                    (updates.unitPrice !== undefined ? updates.unitPrice : item.unitPrice)
                  : item.totalPrice,
            };

            return updatedItem;
          });

          // Recalculate cart total
          const newTotal = updatedItems.reduce((sum, item) => sum + item.totalPrice, 0);

          return {
            ...cart,
            items: updatedItems,
            totalAmount: newTotal,
          };
        }),
      };
    }

    case 'REMOVE_ITEM': {
      const { cartId, itemId } = action.payload;

      return {
        ...state,
        carts: state.carts.map((cart) => {
          if (cart.id !== cartId) return cart;

          const updatedItems = cart.items.filter((item) => item.id !== itemId);

          // Recalculate cart total
          const newTotal = updatedItems.reduce((sum, item) => sum + item.totalPrice, 0);

          return {
            ...cart,
            items: updatedItems,
            totalAmount: newTotal,
          };
        }),
      };
    }

    case 'UPDATE_CUSTOMER': {
      const { cartId, customer } = action.payload;

      return {
        ...state,
        carts: state.carts.map((cart) => (cart.id === cartId ? { ...cart, customer } : cart)),
      };
    }

    case 'UPDATE_CART_DISCOUNT': {
      const { cartId, discount } = action.payload;

      return {
        ...state,
        carts: state.carts.map((cart) => (cart.id === cartId ? { ...cart, discount } : cart)),
      };
    }
    case 'UPDATE_CART': {
      const { cartId, updates } = action.payload;

      return {
        ...state,
        carts: state.carts.map((cart) => {
          if (cart.id !== cartId) return cart;

          return {
            ...cart,
            ...updates,
            totalAmount: cart.items.reduce((sum, item) => sum + item.totalPrice, 0) - (updates.discount || 0),
          };
        }),
      };
    }
    case 'CLEAR_CART': {
      const { cartId } = action.payload;

      return {
        ...state,
        carts: state.carts.map((cart) => {
          if (cart.id === cartId) {
            return {
              id: cart.id,
              name: cart.name,
              status: CartStatus.PRODUCT_SELECTION,
              items: [],
              totalAmount: 0,
            };
          }
          return cart;
        }),
      };
    }

    case 'RESET_ALL': {
      return {
        carts: [],
        activeCartId: null,
      };
    }

    default:
      return state;
  }
};
const saveCartsToStorage = (state: CartState) => {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error('Failed to save carts to localStorage:', error);
  }
};

const loadCartsFromStorage = (): CartState | null => {
  try {
    const storedData = localStorage.getItem(CART_STORAGE_KEY);
    if (storedData) {
      return JSON.parse(storedData);
    }
  } catch (error) {
    console.error('Failed to load carts from localStorage:', error);
    localStorage.removeItem(CART_STORAGE_KEY);
  }
  return null;
};

// Enhanced reducer that saves to localStorage after every state change
const cartReducerWithStorage = (state: CartState, action: CartAction): CartState => {
  const newState = cartReducer(state, action);

  saveCartsToStorage(newState);

  return newState;
};

// Provider component
export const CartPOSProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const getInitialState = (): CartState => {
    const savedState = loadCartsFromStorage();

    if (savedState) {
      return savedState;
    }

    // Default state if nothing in localStorage
    return {
      carts: [
        {
          id: Date.now().toString(),
          name: '#' + Date.now().toString().substring(8, 13),
          status: CartStatus.PRODUCT_SELECTION,
          items: [],
          totalAmount: 0,
        },
      ],
      activeCartId: Date.now().toString(),
    };
  };

  const [state, dispatch] = useReducer(cartReducerWithStorage, getInitialState());

  useEffect(() => {
    if (state.carts.length > 0 && !state.activeCartId) {
      dispatch({ type: 'SET_ACTIVE_CART', payload: { id: state.carts[0].id } });
    }
  }, [state.carts, state.activeCartId]);

  // Helper function to get the active cart
  const getActiveCart = (): Cart | null => {
    if (!state.activeCartId) return null;
    return state.carts.find((cart) => cart.id === state.activeCartId) || null;
  };

  // Actions
  const addCart = (name: string) => {
    dispatch({ type: 'ADD_CART', payload: { name } });
  };

  const removeCart = (id: string) => {
    dispatch({ type: 'REMOVE_CART', payload: { id } });
  };

  const setActiveCart = (id: string) => {
    dispatch({ type: 'SET_ACTIVE_CART', payload: { id } });
  };

  const updateCartStatus = (id: string, status: CartStatus) => {
    dispatch({ type: 'UPDATE_CART_STATUS', payload: { id, status } });
  };

  const addItem = useCallback(
    (
      productVersion: ProductVersion,
      unit: Unit,
      quantity: number,
      unitPrice?: number,
      attribute?: ProductAttribute,
      cartId?: string,
      saleItemDimensions?: SaleItemDimension[],
    ) => {
      const targetCartId = cartId || state.activeCartId;
      if (!targetCartId) return;

      const price = unitPrice !== undefined ? unitPrice : productVersion.salePrice || 0;

      dispatch({
        type: 'ADD_ITEM',
        payload: {
          cartId: targetCartId,
          item: {
            productVersion,
            name:
              [productVersion?.name, productVersion?.color, productVersion?.dimension, productVersion.sizeSpec]
                .filter((product) => product !== undefined && product !== null && product !== '')
                .join(' - ') || '',
            unit,
            quantity,
            unitPrice: price,
            attribute,
            totalPrice: quantity * price,
            saleItemDimesions: saleItemDimensions || [],
          },
        },
      });
    },
    [state.activeCartId, dispatch],
  );

  const updateItem = (itemId: string, updates: Partial<CartItem>) => {
    const activeCart = getActiveCart();
    if (!activeCart) return;

    dispatch({
      type: 'UPDATE_ITEM',
      payload: {
        cartId: activeCart.id,
        itemId,
        updates,
      },
    });
  };

  const removeItem = (itemId: string) => {
    const activeCart = getActiveCart();
    if (!activeCart) return;

    dispatch({
      type: 'REMOVE_ITEM',
      payload: {
        cartId: activeCart.id,
        itemId,
      },
    });
  };

  const updateCustomer = useCallback(
    (customer: Cart['customer'], cartId?: string) => {
      const targetCartId = cartId || state.activeCartId;
      if (!targetCartId) return;

      dispatch({
        type: 'UPDATE_CUSTOMER',
        payload: {
          cartId: targetCartId,
          customer,
        },
      });
    },
    [state.activeCartId, dispatch],
  );

  const updateDiscount = (discount: number) => {
    const activeCart = getActiveCart();
    if (!activeCart) return;

    dispatch({
      type: 'UPDATE_CART_DISCOUNT',
      payload: {
        cartId: activeCart.id,
        discount,
      },
    });
  };
  const updateCart = (cartId: string, updates: Partial<Cart>) => {
    dispatch({
      type: 'UPDATE_CART',
      payload: {
        cartId: cartId,
        updates,
      },
    });
  };
  const clearCart = () => {
    const activeCart = getActiveCart();
    if (!activeCart) return;

    dispatch({
      type: 'CLEAR_CART',
      payload: {
        cartId: activeCart.id,
      },
    });
  };

  const resetAll = () => {
    dispatch({ type: 'RESET_ALL' });
  };

  const contextValue: CartPOSContextType = {
    state,
    addCart,
    removeCart,
    setActiveCart,
    updateCartStatus,
    addItem,
    updateItem,
    removeItem,
    updateCustomer,
    updateDiscount,
    updateCart,
    clearCart,
    resetAll,
    getActiveCart,
  };

  return <CartPOSContext.Provider value={contextValue}>{children}</CartPOSContext.Provider>;
};
export const clearCartStorage = () => {
  try {
    localStorage.removeItem(CART_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear cart storage:', error);
  }
};
// Custom hook for consuming the context
export const useCart = () => {
  const context = useContext(CartPOSContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
